import { useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { VERTICALS } from '../../constants/uriConstants.ts';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';

import { type useShouldShowSimilarSoldItems_viewer$key } from './__generated__/useShouldShowSimilarSoldItems_viewer.graphql.ts';
import {
    type useShouldShowSimilarSoldItems_item$key,
    type useShouldShowSimilarSoldItems_item$data,
} from './__generated__/useShouldShowSimilarSoldItems_item.graphql.ts';

const viewerFragment = graphql`
    fragment useShouldShowSimilarSoldItems_viewer on Viewer {
        ... @include(if: $isPdpSimilarSoldItemsModuleVariant) {
            similarSoldItemsConnection(itemId: $itemId, first: 4) @include(if: $afterDisplay) {
                totalResults
            }
        }
    }
`;

const itemFragment = graphql`
    fragment useShouldShowSimilarSoldItems_item on Item {
        ... @include(if: $isPdpSimilarSoldItemsModuleVariant) {
            vertical
            categoryL2: getCategoriesByLevel(level: 2) {
                key
            }
            pricingGuidance(
                isTrade: $isTrade
                buyerId: $userId
                userCountryCode: $userCountryCode
                priceBookName: $priceBookName
                isBuyerEstimateEnabled: true
            ) @include(if: $afterDisplay) {
                price {
                    convertedAmountList {
                        amount
                        currency
                    }
                }
                minAmount {
                    convertedAmountList {
                        amount
                        currency
                    }
                }
                maxAmount {
                    convertedAmountList {
                        amount
                        currency
                    }
                }
            }
        }
    }
`;

const MIN_SIMILAR_SOLD_ITEMS = 3;

export const useShouldShowSimilarSoldItems = ({
    viewer: viewerRef,
    item: itemRef,
    afterDisplay,
    currency,
}: {
    viewer: useShouldShowSimilarSoldItems_viewer$key | null;
    item: useShouldShowSimilarSoldItems_item$key | null;
    afterDisplay?: boolean | null;
    currency: string;
}): {
    shouldShowSimilarSoldItemsModule: boolean;
    isBelowMinPriceEstimate: boolean;
} => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const { isPdpSimilarSoldItemsModuleVariant } = useServerVarsContext();
    const totalResults = viewer?.similarSoldItemsConnection?.totalResults || 0;
    const { pricingGuidance, vertical, categoryL2 } = item || {};
    const { price: itemPrice, minAmount, maxAmount } = pricingGuidance || {};

    const hasEnoughSimilarSoldItems =
        isPdpSimilarSoldItemsModuleVariant &&
        afterDisplay &&
        totalResults >= MIN_SIMILAR_SOLD_ITEMS;

    // https://1stdibs.atlassian.net/browse/PRICING-355
    const isFashionVertical = vertical === VERTICALS.FASHION;
    const isWatchesCategoryL2 = categoryL2?.some(cat => cat?.key === 'J_WAT');

    const { shouldShowSimilarSoldItemsModule, isBelowMinPriceEstimate } = useMemo(() => {
        if (!item || isFashionVertical || isWatchesCategoryL2 || !hasEnoughSimilarSoldItems) {
            return { shouldShowSimilarSoldItemsModule: false, isBelowMinPriceEstimate: false };
        }

        function getPriceAmountByCurrency({
            price,
        }: {
            price:
                | NonNullable<useShouldShowSimilarSoldItems_item$data['pricingGuidance']>['price']
                | undefined;
        }): number {
            const priceAmount = (price?.convertedAmountList || []).find(
                convertedAmount => convertedAmount?.currency === currency
            );
            return Math.floor(priceAmount?.amount || 0);
        }

        const rangeMin = getPriceAmountByCurrency({ price: minAmount });
        const rangeMax = getPriceAmountByCurrency({ price: maxAmount });
        const currentPrice = getPriceAmountByCurrency({ price: itemPrice });

        return {
            shouldShowSimilarSoldItemsModule: currentPrice <= rangeMax,
            isBelowMinPriceEstimate: currentPrice < rangeMin,
        };
    }, [
        hasEnoughSimilarSoldItems,
        isWatchesCategoryL2,
        isFashionVertical,
        currency,
        itemPrice,
        minAmount,
        maxAmount,
        item,
    ]);

    return {
        shouldShowSimilarSoldItemsModule,
        isBelowMinPriceEstimate,
    };
};
